import { AllowedLocationResponse } from "../../common/api/Models/AllowedLocationResponse";
import { CreditRequestReasonResponse } from "../../common/api/Models/CreditRequestReasonResponse";
import { FeatureConfiguration } from "../../common/api/Models/FeatureConfiguration";
import { LocationSummaryResponse } from "../../common/api/Models/LocationSummaryResponse";
import { PrinterSummaryResponse } from "../../common/api/Models/PrinterSummaryResponse";
import { ProductGroupResponse } from "../../common/api/Models/ProductGroupResponse";
import { VendorResponse } from "../../common/api/Models/VendorResponse";
import { SnackbarAlertState } from "../../util/snackBarAlert";

/**
 * Status count for badge items
 */
export interface StatusCount {
	status: string;
	count: number;
}

/**
 * Failure count for badge items
 */
export interface FailureCount {
	failureReason: string;
	count: number;
}

/**
 * Badge state for displaying counts in the app bar and drawer menu
 */
export interface BadgeState {
	creditRequests: StatusCount[];
	receives: StatusCount[];
	receiveFailures: FailureCount[];
	transfers: StatusCount[];
	ediOutstanding?: number;
}

/**
 * Authorized locations state
 */
export interface AuthorizedLocations {
	ids: number[];
	primary: number;
	list: AllowedLocationResponse[];
}

/**
 * Physical location state
 */
export interface PhysicalLocation {
	[key: string]: unknown;
}

/**
 * Locations state for the application
 */
export interface LocationsState {
	authorised: AuthorizedLocations;
	physical: PhysicalLocation;
	default: number;
	isLoaded: boolean;
	allLocations: LocationSummaryResponse[];
}

/**
 * User information stored in the page context
 */
export interface UserInformation {
	staffId: string;
	name: string;
	countryCode: string;
	clientIp: string;
}

/**
 * Printer state stored in the page context
 */
export interface PrinterState {
	id: string;
	name: string;
	outputType: string;
}

/**
 * Commercial account for selection
 */
export interface CommercialAccount {
	value: string;
	label: string;
}

/**
 * Main page context state interface
 * Contains all application-wide state managed through React Context
 */
export interface PageContextState {
	/** Whether the side menu is open */
	isOpenMenu: boolean;

	/** Current user's selected location ID */
	userLocation: number;

	/** Badge counts for various items */
	badge: BadgeState;

	/** Whether printers have been retrieved */
	hasRetrievedPrinters: boolean;

	/** Whether application data has been retrieved */
	hasRetrievedApplicationData: boolean;

	/** Available printers */
	printers: PrinterSummaryResponse[];

	/** Currently selected printer */
	printer?: PrinterState | null;

	/** Currently selected printer ID */
	selectedPrinterId?: string;

	/** Currently selected label printer ID */
	selectedLabelPrinterId?: string;

	/** Available product groups */
	productGroups: ProductGroupResponse[];

	/** Available vendors */
	vendors: VendorResponse[];

	/** Feature configuration for enabling/disabling features per location */
	featureConfiguration: FeatureConfiguration;

	/** Locations state */
	locations: LocationsState;

	/** Snackbar alert state */
	snackBarAlert: SnackbarAlertState;

	/** Current user information */
	userInformation: UserInformation;

	/** Set of put away location IDs */
	putAwayLocations: Set<string>;

	/** Available commercial accounts */
	commercialAccounts: string[] | CommercialAccount[];

	/** Available carriers for shipping */
	carriers: string[];

	/** Tenancy-configured tax rate (e.g. 0.10 AU, 0.15 NZ) used for freight Inc/Tax calculations */
	taxRate: number;

	/** User allowed locations */
	userAllowedLocations?: AllowedLocationResponse[];

	/** Fulfilment centre location IDs */
	fulfilmentCentreLocations?: number[];

	/** Credit request reasons */
	creditRequestReasons?: CreditRequestReasonResponse[];
}

/**
 * Function type for updating the page context
 * @param data - Partial state to merge with current state
 * @param callback - Optional callback to execute after state update
 */
export type UpdatePageContextFunction = (
	data: Partial<PageContextState>,
	callback?: (() => void) | null
) => void;

/**
 * Type for the PageContext value tuple
 * [0] - Current state
 * [1] - Update function
 */
export type PageContextType = [PageContextState, UpdatePageContextFunction];

/**
 * Default initial state for the page context
 */
export const defaultPageContextState: PageContextState = {
	isOpenMenu: false,
	userLocation: 0,
	badge: {
		creditRequests: [],
		receives: [],
		receiveFailures: [],
		transfers: [],
	},
	hasRetrievedPrinters: false,
	hasRetrievedApplicationData: false,
	printers: [],
	printer: null,
	selectedPrinterId: "",
	selectedLabelPrinterId: "",
	productGroups: [],
	vendors: [],
	featureConfiguration: {
		arrivals: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		adhocCreditRequest: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		productLabelAdhocPrint: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		transfers: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		outstandingEdiInvoices: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		inboundLocationAssignment: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		productLabelAutoPrint: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		priorityStock: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		commercialAccountSelection: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		customerOrderAllocationAndAdvancement: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		skipCustomerOrderAllocation: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		partReceive: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
		captureFreightCost: { enabledLocations: [], enabledReceiveTypes: [], enabledTransferReasons: null },
	},
	locations: {
		authorised: {
			ids: [],
			primary: -1,
			list: [],
		},
		physical: {},
		default: 0,
		isLoaded: false,
		allLocations: [],
	},
	snackBarAlert: {
		isOpen: false,
		message: "",
		severity: "info",
	},
	userInformation: {
		staffId: "",
		name: "",
		countryCode: "",
		clientIp: "",
	},
	putAwayLocations: new Set<string>(),
	commercialAccounts: [],
	carriers: [],
	taxRate: 0,
	userAllowedLocations: [],
	fulfilmentCentreLocations: [],
	creditRequestReasons: [],
};
